import React, { FC, useEffect, useMemo } from "react";

import { Stack } from "react-bootstrap";

import { Authenticator, Heading, useAuthenticator } from "@aws-amplify/ui-react";
import { Amplify } from "aws-amplify";
import { fetchMFAPreference, updateMFAPreference } from "aws-amplify/auth";
import { useMatch, useNavigate } from "react-router-dom";

import { useAuthenticationContext } from "@context/authentication";
import { DashboardFooter } from "@pages/dashboard/DashboardFooter";
import { DashboardHeader } from "@pages/dashboard/DashboardHeader";
import { RegisterPageOutline } from "@pages/register/RegisterPageOutline";
import { isNullOrUndefined } from "@util/index";

import { amplifyConfig } from "../../config/amplify";
import { ROUTES } from "../../routes";

import { CustomizedSignIn } from "./CustomizedSignIn";

import "./External.scss";

type InitialStateType = "signIn" | "signUp";

const RegisterFormHeader = () => {
  return <Heading level={3}>Create new account</Heading>;
};

const CustomAuthenticator: FC<{ initialState: InitialStateType }> = ({ initialState }) => {
  return (
    <div className={window.navigator.userAgent.includes("Windows") ? "amplify" : ""}>
      <Authenticator
        initialState={initialState}
        loginMechanisms={["email"]}
        components={{
          SignUp: {
            Header: RegisterFormHeader,
          },
        }}
        formFields={{
          confirmSignUp: {
            confirmation_code: {
              placeholder: "",
              label: "Confirmation Code",
              maxLength: 6,
            },
          },
          confirmSignIn: {
            confirmation_code: {
              placeholder: "",
              label: "Verification Code",
              maxLength: 6,
            },
          },
          confirmResetPassword: {
            confirmation_code: {
              placeholder: "",
              label: "Confirmation Code",
              maxLength: 6,
            },
          },
          signUp: {
            phone_number: {
              dialCode: "+61", // default dial code
            },
          },
        }}
        services={{
          async validateCustomSignUp(formData) {
            if (formData.email && formData.password && formData.password === formData.email) {
              return {
                password: "Password cannot be the same as the email address",
              };
            }
          },
        }}
      ></Authenticator>
    </div>
  );
};

export const ExternalSignIn = () => {
  const { auth } = useAuthenticationContext();

  const { authStatus, toSignIn, toSignUp } = useAuthenticator((context) => [context.user]);
  const { route } = useAuthenticator((context) => [context.route]);

  const navigate = useNavigate();
  const isRegister = !isNullOrUndefined(useMatch(ROUTES.ExternalRegister.path));
  const initialState: InitialStateType = isRegister ? "signUp" : "signIn";
  const displayNavigator = route !== "forgotPassword" && route !== "confirmResetPassword";

  const isConfirmSignUp = useMemo(() => {
    return route === "confirmSignUp";
  }, [route]);

  const updateMfaPreferenceIfNotSet = async () => {
    try {
      const mfaPreference = await fetchMFAPreference();
      //Update MFA to email if it is not set already. If it is already set, do nothing.
      if (!mfaPreference?.preferred) {
        await updateMFAPreference({ sms: "ENABLED", email: "PREFERRED" });
        console.log("MFA Preference updated successfully!");
      }
    } catch (error) {
      console.error(error);
    }
  };
  /***
   * this useEffect fixes the defect that when user navigates to forgotPassword or confirmResetPassword
   * then head to home page -> back to register URL by clicking register,
   * the authenticator will stay in forgotPassword or confirmResetPassword
   * and for unknown reason, @function toSignUp does not work when the authenticator is in forgotPassword or confirmResetPassword
   * so using @function toSignIn to redirect user and let other useEffect to update the authenticator
   */
  useEffect(() => {
    const specialRoute = route === "forgotPassword" || route === "confirmResetPassword";
    if (isRegister && specialRoute) {
      toSignIn();
    }
  }, [isRegister, route, toSignIn]);

  useEffect(() => {
    if (isRegister) {
      toSignUp();
    } else {
      toSignIn();
    }
    // Only run when entering the page, sign in redirection will be done in navigator
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toSignUp, toSignIn]);

  useEffect(() => {
    if (authStatus === "authenticated" && auth) {
      updateMfaPreferenceIfNotSet();

      // if user is registered to cognito but not in rrsUser db
      if (!auth.currentUserData?.participant?.participant_id) {
        navigate(ROUTES.RegisterSubmission.path);
      } //  if user is registered in cognito and in rrsUser db when being created by a VA/MS they need to complete registration form.
      else if (
        auth.currentUserData?.participant?.participant_id &&
        auth.currentUserData.participant.participant_status === "INCOMPLETE"
      ) {
        navigate(ROUTES.RegisterSubmission.path);
      } else {
        navigate(ROUTES.Dashboard.path);
      }
    } else {
      Amplify.configure(amplifyConfig.external);
    }
  }, [auth, authStatus, navigate]);

  return (
    <Stack className="external-signin-container" direction="horizontal">
      {isConfirmSignUp ? (
        <>
          <DashboardHeader showResaleCart={false} />
          <div className="confirm-sign-up-container">
            <CustomAuthenticator initialState={initialState} />
          </div>
          <DashboardFooter />
        </>
      ) : isRegister ? (
        // register flow will still use the default amplify ui
        <RegisterPageOutline showLogIn={isRegister} displayNavigator={displayNavigator}>
          <CustomAuthenticator initialState={initialState} />
        </RegisterPageOutline>
      ) : (
        <CustomizedSignIn />
      )}
    </Stack>
  );
};
